import { Capacitor, CapacitorHttp, HttpHeaders, HttpResponse } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { StatusBar, Style } from '@capacitor/status-bar';
import { AndroidFullScreen } from '@ionic-native/android-full-screen';
import { ScreenOrientation } from '@ionic-native/screen-orientation';
import Debug from 'debug';
import { Browser } from '@capacitor/browser';

import { isAndroid, isIOS, supportsLandscape } from '~common/device';
export { getNormalizedLocationPath } from '~common/utils';

import i18n from 'lib/i18n';
import { addHeaderAccessors } from './nativeHeaders';
import safeAreaService, { ANDROID_EDGE_TO_EDGE_VERSION_THRESHOLD } from './safeArea';
import { getAndroidVersion } from './device';
import history from './history';

const debug = Debug('ovosplay:native');

// there is 8px gap between the keyboard and the bottom of the screen on android 14 and lower when keyboard is opened
const ANDROID_KEYBOARD_GAP_PX = 8;

type CustomFetchResponse = HttpResponse & {
  headers: Headers;
  ok: boolean;
  text: () => Promise<string>;
  json: () => Promise<object>;
  statusText: string;

  // props from fetch.Response which are not implemented
  // (why capacitor does not respond with 1:1 compliant fetch's Response type, is beyond me)
  arrayBuffer: never;
  blob: never;
  body: never; // fetch.Response.body can only be a `ReadableStream<Uint8Array>`, not just `string`
  bodyUsed: never;
  bytes: never;
  clone: never;
  formData: never;
  redirected: never;
  type: never;
};

const defaultCapacitorFetchOptions = {
  method: 'GET',
};

/**
 * This is a very simple wrapper around Capacitors native HTTP functionality
 * to make it useable interchangeably with the regular web fetch (for our use case).
 *
 * Note: Only the most used return values of Window['fetch'] are set here.
 */
const capacitorFetch: (...args: Parameters<Window['fetch']>) => Promise<CustomFetchResponse> = async (url, options = {}) => {
  const { method, headers, body } = Object.assign({}, defaultCapacitorFetchOptions, options);
  let data = body;

  if (typeof url !== 'string') {
    throw new Error('Only use capacitorFetch with (url: string, options: RequestInit)');
  }

  try {
    if (
      typeof body === 'string' &&
      headers && 'content-type' in headers &&
      headers['content-type'] === 'application/json'
    ) {
      data = JSON.parse(body);
    }

    const res = await CapacitorHttp.request({
      url,
      headers: {
        ['x-client-version']: import.meta.env.VITE_APP_VERSION,
        'x-language': i18n.language,
        ...headers,
      } as HttpHeaders,
      data,
      method: method.toUpperCase(),
      webFetchExtra: {
        credentials: 'include',
      },
    });

    addHeaderAccessors(res.headers);

    return <CustomFetchResponse>{
      ...res,
      headers: res.headers as unknown as Headers,
      ok: res.status >= 200 && res.status <= 299,
      text: () => Promise.resolve(typeof res.data === 'object' ? JSON.stringify(res.data) : res.data),
      json: () => Promise.resolve(typeof res.data === 'object' ? res.data : JSON.parse(res.data)),
      statusText: `${res.status}`,
    };
  } catch (err) {
    console.error('An error occurred during capacitorFetch:', err);

    throw err;
  }
};

export const fetchRequest =
  !Capacitor.isNativePlatform() // use fetch if we're in non-native env
  || localStorage.getItem('useFetch') === 'true' // use fetch if `useFetch="true"` is set in localStorage
    ? (info: RequestInfo | URL, init?: RequestInit) => fetch(info, {
    ...init,
    credentials: 'include',
    headers: {
      ['x-client-version']: import.meta.env.VITE_APP_VERSION,
      'x-language': i18n.language,
      ...(init?.headers || {}),
    },
  })
    : capacitorFetch;

/**
 * Sets up various native app related functionality, such as hiding status bar,
 * configuring keyboard accessory bar and handling immersive mode on android.
 */
export function setupBasicNativeAppFunctionality() {
  if (Capacitor.isNativePlatform()) {
    // initialize safe area service first to ensure originalInsets are set before keyboard listeners
    safeAreaService.initialize().catch((error) => {
      console.error('Failed to initialize safe area service:', error);
    });

    if (!supportsLandscape()) {
      ScreenOrientation.lock(ScreenOrientation.ORIENTATIONS.PORTRAIT);
    }

    // set status bar font color to white to be visible on the black notch background
    StatusBar.setStyle({ style: Style.Dark });

    if (isAndroid()) {
      StatusBar.hide();
    }

    Keyboard.addListener('keyboardDidShow', async () => {
      const activeElement = document.activeElement;
      // Skip scrollIntoView for chat message inputs — useKeyboardStickyHeader
      // handles repositioning, and centering the input pushes the header out.
      // See data-chat-message-input attribute in MessageInput.tsx.
      if (activeElement && !activeElement.hasAttribute('data-chat-message-input')) {
        activeElement.scrollIntoView({
          behavior: 'instant',
          block: 'center', // scroll to center so the input fields at the bottom are not covered by sticky bottom elements
        });
      }

      if (isIOS()) {
        await safeAreaService.overrideInset('bottom', 0);
      }

      const androidVersion = await getAndroidVersion();
      if (androidVersion !== null && androidVersion < ANDROID_EDGE_TO_EDGE_VERSION_THRESHOLD) {
        const currentBottomInset = await safeAreaService.getDeviceInsets().bottom;
        await safeAreaService.overrideInset('bottom', currentBottomInset - ANDROID_KEYBOARD_GAP_PX);
      }
    });

    Keyboard.addListener('keyboardWillHide', async () => {
      if (isIOS()) {
        await safeAreaService.restoreInset('bottom');
      }

      const androidVersion = await getAndroidVersion();
      if (androidVersion !== null && androidVersion < ANDROID_EDGE_TO_EDGE_VERSION_THRESHOLD) {
        await safeAreaService.restoreInset('bottom');
      }
    });

    if (isIOS()) {
      debug('Setting up iOS specific functionality...');

      // accessory bar is only available on iOS devices
      Keyboard.setAccessoryBarVisible({ isVisible: true });

      /**
       * route all internal hash links (#/...) through history.push on iOS.
       *
       * iOS WKWebView freezes when an internal hash route is navigated via the
       * native link behavior or via window.location.href assignment. This affects
       * both default-target links and target="_blank" links (which iOS would
       * otherwise try to hand off to Safari). Intercepting them here and pushing
       * to our hash history singleton avoids the freeze and keeps navigation
       * inside the React Router tree.
       *
       * The defaultPrevented guard lets React Router <Link> handlers run first
       * without us double-pushing.
       *
       * handle absolute URLs with Browser.open
       */
      document.addEventListener('click', async (event) => {
        const targetElement = event.target as HTMLElement;
        const anchorElement = targetElement.closest('a');

        if (anchorElement) {
          const href = anchorElement.getAttribute('href') || '';

          if (href.startsWith('#/') && !event.defaultPrevented) {
            event.preventDefault();
            history.push(href.slice(1));
            return;
          }

          if (href.startsWith('https://') || href.startsWith('http://')) {
            event.preventDefault();
            try {
              await Browser.open({ url: href });
            } catch (error) {
              console.error('Error opening link:', error);
            }
          }
        }
      });
    }

    /**
    * Calling this ensures that our app is in immersive mode on android which
    * should properly hide the menubar (even after keyboard input)
    * https://github.com/mesmotronic/cordova-plugin-fullscreen
    */
    if (isAndroid() && AndroidFullScreen) {
      AndroidFullScreen.immersiveMode();
    }
  }
}

export function hideKeyboard() {
  if (document.activeElement instanceof HTMLElement && document.activeElement.nodeName === 'INPUT') {
    document.activeElement.blur();
  }
}

/**
 * Attaches ?native= with mobile app id to allow proper redirect back to app from service side
 */
export function getMobileRedirectUrl(url: string) {
  return `${url}?native=${isIOS() ? window.MOBILE_APP_IOS_APPID : window.MOBILE_APP_ANDROID_APPID}`;
}

export const removeIfFalsyOrEmpty = <T>(obj: T, keysToRemove: (keyof T)[]) => {
  for (const key of keysToRemove) {
    const val = obj[key];
    if (
      !val ||
      (Array.isArray(val) && val.length <= 0) ||
      key === '__typename'
    ) delete obj[key];
  }

  return obj;
};
